import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import RichContent from '../../components/public/RichContent';
import './AboutPage.css';

const INTERNAL_CONTENT_SLUGS = new Set(['banners', 'representatives']);

// If content is a full HTML document, render it in a sandboxed iframe.
// Otherwise render as an inline HTML fragment.
function isFullDocument(html) {
  return /<!DOCTYPE\s+html/i.test(html) || /<html[\s>]/i.test(html);
}

export default function CustomPageView() {
  const { pageSlug, tenantSlug } = useParams();
  const { tenantData, loading } = useTenant();
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div style={{ padding: '60px 20px', textAlign: 'center', color: '#888' }}>लोड होत आहे...</div>;

  if (INTERNAL_CONTENT_SLUGS.has(String(pageSlug || '').toLowerCase())) {
    return <Navigate to={`/gp/${tenantSlug}`} replace />;
  }

  const page = tenantData?.pages?.find(p => p.slug === pageSlug && p.is_published);

  if (!page) {
    return (
      <div style={{ padding: '80px 20px', textAlign: 'center' }}>
        <h2 style={{ color: '#c0392b' }}>404 — पृष्ठ सापडले नाही</h2>
        <p style={{ color: '#888' }}>तुम्ही शोधत असलेले पृष्ठ अस्तित्वात नाही किंवा अप्रकाशित केले आहे.</p>
        <Link to={`/gp/${tenantSlug}`} style={{ color: '#1a5276', fontWeight: 600 }}>← मुख्यपृष्ठावर जा</Link>
      </div>
    );
  }

  // Build sections: prefer page.sections, fall back to page.content
  const sections = (page.sections && Array.isArray(page.sections) && page.sections.length > 0)
    ? page.sections.sort((a, b) => (a.order || 0) - (b.order || 0))
    : page.content
      ? [{ id: 'legacy', heading: '', content: page.content, order: 0 }]
      : [];

  // No content at all
  if (sections.length === 0) {
    return (
      <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px 60px' }}>
        <h1 style={{ color: '#1a5276', borderBottom: '3px solid #e67e22', paddingBottom: 10 }}>{page.title}</h1>
        <p style={{ color: '#aaa' }}>अजून कोणतीही माहिती नाही.</p>
      </div>
    );
  }

  // Legacy full HTML document → iframe
  if (sections.length === 1 && sections[0].id === 'legacy' && isFullDocument(sections[0].content)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)' }}>
        <iframe
          title={page.title}
          srcDoc={sections[0].content}
          sandbox="allow-scripts allow-same-origin"
          style={{ flex: 1, border: 'none', width: '100%' }}
        />
      </div>
    );
  }

  // Default: all sections expanded initially
  const isExpanded = (id) => expandedSections[id] !== false;

  return (
    <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px 60px' }}>
      <h1 style={{ color: '#1a5276', borderBottom: '3px solid #e67e22', paddingBottom: 10, marginBottom: 20 }}>{page.title}</h1>

      {/* Section navigator */}
      {sections.length > 1 && sections.some(s => s.heading) && (
        <div className="about-section-nav" style={{ marginBottom: 18 }}>
          {sections.filter(s => s.heading).map(sec => (
            <button key={sec.id} className="about-section-nav-item" onClick={() => {
              setExpandedSections(prev => ({ ...prev, [sec.id]: true }));
              document.getElementById(`page-sec-${sec.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}>{sec.heading}</button>
          ))}
        </div>
      )}

      {/* Sections */}
      {sections.map(sec => (
        <div key={sec.id} id={`page-sec-${sec.id}`} className="about-card" style={{ marginBottom: 16 }}>
          {sec.heading && (
            <div className="about-card-header" onClick={() => toggleSection(sec.id)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="about-card-title">{sec.heading}</h2>
              <span className={`about-card-chevron ${isExpanded(sec.id) ? 'about-card-chevron--open' : ''}`}>▼</span>
            </div>
          )}
          <div className={`about-card-body-wrapper ${!sec.heading || isExpanded(sec.id) ? 'about-card-body-wrapper--open' : ''}`}>
            <RichContent html={sec.content} className="about-card-body" />
          </div>
        </div>
      ))}
    </div>
  );
}
